import { mapValues } from 'lodash';
import { CREATE_REPLY_SUCCESS } from './replies/replies-crud-actions';
import { CREATE_COMMENT_SUCCESS } from './top-level-comments/top-level-comments-crud-actions';
import { unreachable } from '../../../ts-utils';
import { getCommentStateById } from './comments-selectors';
function getCommentParentId(state, resourceId, commentId) {
    const comment = getCommentStateById(state, resourceId)(commentId);
    return comment?.type === 'REPLY_READY' ? comment.parentId : '';
}
export const createCommentsWatcherConfig = () => {
    let onCreate;
    return {
        middleware: (store) => (next) => (action) => {
            const state = store.getState();
            const membersCache = state.members.membersCache;
            if (action.type === CREATE_COMMENT_SUCCESS) {
                const reactions = getCommentReactionsState({
                    resourceState: state.comments[action.payload.resourceId],
                    commentId: action.payload.comment.commentId,
                });
                onCreate?.({
                    type: 'COMMENT',
                    ...getCommentFields({
                        comment: action.payload.comment,
                        members: membersCache,
                        reactions,
                    }),
                });
            }
            if (action.type === CREATE_REPLY_SUCCESS) {
                const { resourceId, localId } = action.payload;
                const reactions = getCommentReactionsState({
                    resourceState: state.comments[action.payload.resourceId],
                    commentId: action.payload.comment.commentId,
                });
                onCreate?.({
                    type: 'REPLY',
                    parentId: getCommentParentId(state, resourceId, localId),
                    ...getCommentFields({
                        comment: action.payload.comment,
                        members: membersCache,
                        reactions,
                    }),
                });
            }
            return next(action);
        },
        createWatcher: (store) => ({
            onCreate: (onCommentCreate) => {
                onCreate = onCommentCreate;
            },
            getLoadedComments: () => {
                const state = store.getState();
                return toExposedState(state.comments, state.members);
            },
        }),
    };
};
const toExposedState = (comments, members) => mapValues(comments, (resourceState) => {
    if (!resourceState) {
        return { type: 'NONE' };
    }
    switch (resourceState.type) {
        case 'ERROR':
        case 'PENDING': {
            return {
                type: resourceState.type,
            };
        }
        case 'READY': {
            return {
                type: 'READY',
                comments: toExposedCommentsList(resourceState, members.membersCache),
            };
        }
        default: {
            throw unreachable(resourceState);
        }
    }
});
function toExposedCommentsList(resourceState, members) {
    return Object.keys(resourceState.commentsById).reduce((acc, commentId) => {
        const state = resourceState.commentsById[commentId];
        switch (state.type) {
            case 'COMMENT_READY': {
                acc[commentId] = {
                    type: 'COMMENT',
                    ...getCommentFields({
                        comment: state.comment,
                        members,
                        reactions: state.reactions.reactions,
                    }),
                };
                break;
            }
            case 'REPLY_READY': {
                acc[commentId] = {
                    type: 'REPLY',
                    parentId: state.parentId,
                    ...getCommentFields({
                        comment: state.comment,
                        members,
                        reactions: state.reactions.reactions,
                    }),
                };
                break;
            }
        }
        return acc;
    }, {});
}
function getCommentFields({ comment, members, reactions, }) {
    const author = comment.author?.identityId
        ? comment.author.identityType === 'ANONYMOUS'
            ? { type: 'ANONYMOUS', id: comment.author.identityId }
            : {
                type: 'MEMBER',
                id: comment.author?.identityId,
                profile: members[comment.author?.identityId]?.profile,
            }
        : undefined;
    return {
        commentId: comment.commentId,
        createdAt: comment.createdAt,
        updatedAt: comment.updatedAt,
        upvoteCount: comment.voteSummary.upvoteCount,
        downvoteCount: comment.voteSummary.downvoteCount,
        score: comment.voteSummary.score,
        content: comment.content,
        isMarked: comment.isMarked,
        replyCount: comment.repliesCount,
        memberProfile: comment.author?.identityType === 'MEMBER'
            ? members[comment.author.identityId]?.profile
            : undefined,
        author,
        reactions,
    };
}
const getCommentReactionsState = ({ resourceState, commentId, }) => {
    if (resourceState?.type === 'READY') {
        const commentState = resourceState.commentsById[commentId];
        if (commentState && 'reactions' in commentState) {
            return commentState.reactions.reactions;
        }
    }
};
//# sourceMappingURL=created-comments-watcher.js.map